import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Link,
  Paper,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
} from "@mui/material";
import { ArrowBack, Email, Phone, CheckCircle } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";

const ForgotPassword = () => {
  const [resetMethod, setResetMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  const navigate = useNavigate();

  const handleMethodChange = (event, newMethod) => {
    if (newMethod !== null) {
      setResetMethod(newMethod);
      setError("");
      setSuccess(false);
      setOtpSent(false);
      setEmail("");
      setPhoneNumber("");
      setOtp("");
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex =
      /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    return phoneRegex.test(phone) && phone.replace(/\D/g, "").length >= 8;
  };

  const handleSendReset = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      if (resetMethod === "email") {
        if (!email.trim()) {
          setError("Please enter your email address");
          setIsLoading(false);
          return;
        }

        if (!validateEmail(email)) {
          setError("Please enter a valid email address");
          setIsLoading(false);
          return;
        }

        const { data } = await axios.post(
          `${process.env.REACT_APP_API_GATEAWAY}/auth/forgot-password`,
          { email: email.trim() }
        );

        setSuccess(true);
        toast.success("Password reset link sent to your email!");
      } else {
        if (!phoneNumber.trim()) {
          setError("Please enter your phone number");
          setIsLoading(false);
          return;
        }

        if (!validatePhone(phoneNumber)) {
          setError("Please enter a valid phone number");
          setIsLoading(false);
          return;
        }

        const { data } = await axios.post(
          `${process.env.REACT_APP_API_GATEAWAY}/auth/send-sms-verification`,
          { phone: phoneNumber.trim() }
        );

        setOtpSent(true);
        setResendTimer(60);
        toast.success("OTP sent to your phone number!");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to send reset information. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!otp.trim() || otp.length !== 4) {
      setError("Please enter a valid 4-digit OTP");
      setIsLoading(false);
      return;
    }

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/auth/verify-sms-code`,
        {
          phone: phoneNumber.trim(),
          code: otp.trim(),
        }
      );

      if (data.token && data.resetUrl) {
        setSuccess(true);
        toast.success("OTP verified successfully!");
        // navigate(`${data.resetUrl}`);
        window.location.href = data.resetUrl;
      } else {
        navigate("/reset-password");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Invalid OTP. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError("");
    setIsLoading(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/auth/send-sms-verification`,
        {
          phone: phoneNumber.trim(),
        }
      );
      toast.success("New OTP sent to your phone!");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to resend OTP";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #111827 0%, #121b30ff 100%)",
        padding: 2,
      }}
    >
      <Paper
        elevation={24}
        sx={{
          padding: 4,
          width: "100%",
          maxWidth: 480,
          borderRadius: 2,
          backgroundColor: "#1f2937",
        }}
      >
        <Stack spacing={3}>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#00BFFF",
                marginBottom: 1,
              }}
            >
              Reset Password
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#9ca3af",
              }}
            >
              {success
                ? resetMethod === "email"
                  ? "Check your email for reset instructions"
                  : "Verification successful! Redirecting..."
                : otpSent
                ? "Enter the OTP sent to your phone"
                : "Choose your preferred reset method"}
            </Typography>
          </Box>

          {!success && !otpSent && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <ToggleButtonGroup
                value={resetMethod}
                exclusive
                onChange={handleMethodChange}
                sx={{
                  backgroundColor: "#374151",
                  "& .MuiToggleButton-root": {
                    color: "#9ca3af",
                    borderColor: "#4b5563",
                    padding: "10px 24px",
                    "&.Mui-selected": {
                      backgroundColor: "#00BFFF",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#0099CC",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "#4b5563",
                    },
                  },
                }}
              >
                <ToggleButton value="email">
                  <Email sx={{ mr: 1, fontSize: 20 }} />
                  Email
                </ToggleButton>
                <ToggleButton value="phone">
                  <Phone sx={{ mr: 1, fontSize: 20 }} />
                  Phone
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                backgroundColor: "#7f1d1d",
                color: "#fecaca",
                "& .MuiAlert-icon": { color: "#fecaca" },
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              icon={<CheckCircle />}
              sx={{
                backgroundColor: "#065f46",
                color: "#d1fae5",
                "& .MuiAlert-icon": { color: "#d1fae5" },
              }}
            >
              {resetMethod === "email"
                ? "Password reset link has been sent to your email address. Please check your inbox and follow the instructions."
                : "OTP verified successfully! Redirecting to password reset..."}
            </Alert>
          )}

          {!success && !otpSent && (
            <form onSubmit={handleSendReset}>
              <Stack spacing={3}>
                {resetMethod === "email" ? (
                  <TextField
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    fullWidth
                    autoComplete="email"
                    disabled={isLoading}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: "#9ca3af" }} />
                        </InputAdornment>
                      ),
                      sx: {
                        color: "#ffffff",
                        backgroundColor: "#374151",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6b7280",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d6c1c1ff",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#00BFFF",
                        },
                        "&.Mui-disabled": {
                          opacity: 0.6,
                        },
                      },
                    }}
                    InputLabelProps={{
                      style: { color: "#ffffff", fontSize: "1.1rem" },
                    }}
                  />
                ) : (
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Phone Number"
                    placeholder="Enter phone number (e.g., 0123456789)"
                    type="tel"
                    fullWidth
                    variant="outlined"
                    value={phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      setPhoneNumber(value);
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData
                        .getData("Text")
                        .replace(/\D/g, "");
                      setPhoneNumber((prev) => (prev + pasted).slice(0, 10));
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") handlePhoneSearchSubmit();
                    }}
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                  />
                )}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isLoading}
                  sx={{
                    backgroundColor: "#00BFFF",
                    color: "#fff",
                    fontWeight: 600,
                    padding: "12px",
                    "&:hover": {
                      backgroundColor: "#0099CC",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#374151",
                      color: "#6b7280",
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : resetMethod === "email" ? (
                    "SEND RESET LINK"
                  ) : (
                    "SEND CODE"
                  )}
                </Button>
              </Stack>
            </form>
          )}

          {!success && otpSent && (
            <form onSubmit={handleVerifyOtp}>
              <Stack spacing={3}>
                <TextField
                  label="Enter OTP"
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 4) setOtp(value);
                  }}
                  required
                  fullWidth
                  disabled={isLoading}
                  variant="outlined"
                  placeholder="1234"
                  inputProps={{
                    maxLength: 4,
                    style: {
                      textAlign: "center",
                      fontSize: "1.5rem",
                      letterSpacing: "0.5rem",
                    },
                  }}
                  InputProps={{
                    sx: {
                      color: "#ffffff",
                      backgroundColor: "#374151",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6b7280",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d6c1c1ff",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00BFFF",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.6,
                      },
                    },
                  }}
                  InputLabelProps={{
                    style: { color: "#ffffff", fontSize: "1.1rem" },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isLoading}
                  sx={{
                    backgroundColor: "#00BFFF",
                    color: "#fff",
                    fontWeight: 600,
                    padding: "12px",
                    "&:hover": {
                      backgroundColor: "#0099CC",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#374151",
                      color: "#6b7280",
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    "VERIFY OTP"
                  )}
                </Button>

                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: "#9ca3af", mb: 1 }}>
                    Didn't receive the code?
                  </Typography>
                  <Button
                    onClick={handleResendOtp}
                    disabled={isLoading || resendTimer > 0}
                    sx={{
                      color: resendTimer > 0 ? "#6b7280" : "#00BFFF",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: resendTimer > 0 ? "none" : "underline",
                      },
                    }}
                  >
                    {resendTimer > 0
                      ? `Resend in ${resendTimer}s`
                      : "Resend OTP"}
                  </Button>
                </Box>
              </Stack>
            </form>
          )}

          <Box sx={{ textAlign: "center" }}>
            <Link
              onClick={() => navigate("/signin")}
              sx={{
                color: "#00BFFF",
                textDecoration: "none",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              <ArrowBack fontSize="small" />
              Back to Sign In
            </Link>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default ForgotPassword;
